/* eslint-disable no-nested-ternary */
import React, { createContext, useContext, useMemo } from "react";

import { useQueries, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { useAppState } from "../../../context/StateContext";
import { Country } from "../../../models/country";
import { Customer } from "../../../models/customer";
import { Document } from "../../../models/document";
import { Milestone } from "../../../models/milestone";
import { NotifyParty } from "../../../models/notify.party";
import { Office } from "../../../models/office";
import { Port } from "../../../models/port";
import { Supplier } from "../../../models/supplier";
import { Warehouse } from "../../../models/warehouse";
import CountryService from "../../../services/country.service";
import CustomerService from "../../../services/customer.service";
import DocumentService from "../../../services/document.service";
import NotifyPartyService from "../../../services/notify.party.service";
import OfficeService from "../../../services/office.service";
import PortService from "../../../services/port.service";
import ShipmentService from "../../../services/shipment.service";
import SupplierService from "../../../services/supplier.service";
import WarehouseService from "../../../services/warehouse.service";
import { CompanyProfile, Status } from "../../../util/enums";
import {
  CountryKeys,
  CustomerKeys,
  DocumentKeys,
  MilestoneKeys,
  NotifyPartyKeys,
  OfficeKeys,
  PortKeys,
  ShipmentResourceKeys,
  SupplierKeys,
  WarehouseKeys,
} from "../../../util/queries";
import { useBookingContext } from "./Booking";

export type ExternalList = {
  resourceCompanyId?: number;
  warehouseList: Warehouse[];
  customerList: Customer[];
  milestoneDates: Milestone[];
  companyDocuments: Document[];
  portList: Port[];
  supplierList: Supplier[];
  officeList: Office[];
  notifyPartyList: NotifyParty[];
  countryList: Country[];
};

interface ShipmentResourcesContextProps extends ExternalList {
  loading: boolean;
}

const ShipmentResourcesContext = createContext<ShipmentResourcesContextProps>(
  {} as ShipmentResourcesContextProps
);

interface ShipmentResourcesProviderProps {
  children: React.ReactChild;
}

type Request = Array<
  [key: string, execute: (args: any) => Promise<AxiosResponse<any, any>>, ...executeArgs: any]
>;

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 5 * 60 * 60 * 1000; // 5 horas
const MAERSK_COMPANY_ID = 1;

const ShipmentResourcesProvider = ({ children }: ShipmentResourcesProviderProps) => {
  const { currentUser, isFMVersion } = useAppState();
  const bookingContext = useBookingContext();
  const bookingId = bookingContext ? bookingContext.booking?.id : undefined;

  const { data: shipmentNetwork, isRefetching: loadingShipmentNetwork } = useQuery({
    queryKey: ShipmentResourceKeys.getShipmentNetwork(bookingId),
    queryFn: async () => {
      const response = await ShipmentService.getNetworkInfos({
        sortBy: JSON.stringify([["id", "ASC"]]),
        shipmentId: bookingId,
        page: 0,
        pageSize: 1,
      });

      if (response && response.data) return response.data[0];
    },
    enabled: !!bookingId && isFMVersion,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  const companyId = useMemo(() => {
    return isFMVersion
      ? shipmentNetwork
        ? shipmentNetwork?.associatedCompanyId
        : undefined
      : currentUser?.company?.profile?.initials === CompanyProfile.SPP
      ? currentUser.company.supplierAssociated?.companyId
      : currentUser?.companyId;
  }, [shipmentNetwork, isFMVersion]);

  const milestonesCompanyId = useMemo(() => {
    if (
      !bookingContext ||
      !bookingContext.booking ||
      !bookingContext?.booking?.purchaseOrders?.length
    )
      return MAERSK_COMPANY_ID;

    const [order] = bookingContext.booking.purchaseOrders;

    return order?.companyId;
  }, [bookingContext]);

  const Resources: Request = useMemo(() => {
    return [
      [
        WarehouseKeys.all,
        WarehouseService.getAll,
        { sortBy: JSON.stringify([["name", "ASC"]]), status: Status.ACTIVE, companyId },
      ],
      [
        CustomerKeys.all,
        CustomerService.getAll,
        { sortBy: JSON.stringify([["id", "ASC"]]), status: Status.ACTIVE, companyId },
      ],
      [
        MilestoneKeys.all,
        ShipmentService.getMilestoneDates,
        { status: Status.ACTIVE, companyId: isFMVersion ? milestonesCompanyId : companyId },
      ],
      [DocumentKeys.all, DocumentService.getAll, { companyId: "", status: Status.ACTIVE }],
      [
        PortKeys.all,
        PortService.getAll,
        {
          attributes: JSON.stringify([
            "id",
            "site",
            "siteCode",
            "city",
            "countryId",
            "locationName",
          ]),
          sortBy: JSON.stringify([["siteCode", "ASC"]]),
          include: JSON.stringify([
            { association: "country", attributes: ["code", "name"] },
            { association: "companyPorts", required: true, where: { companyId } },
          ]),
        },
      ],
      [SupplierKeys.all, SupplierService.getAll, { status: Status.ACTIVE, companyId }],
      // TODO: using companyId fixed but will be refactor when add connections between companies
      [
        OfficeKeys.all,
        OfficeService.getAll,
        {
          include: JSON.stringify([{ association: "port" }]),
          companyId: MAERSK_COMPANY_ID,
          status: Status.ACTIVE,
        },
      ],
      [
        NotifyPartyKeys.all,
        NotifyPartyService.getAll,
        { companyId: MAERSK_COMPANY_ID, status: Status.ACTIVE },
      ],
      [
        CountryKeys.all,
        CountryService.getAll,
        {
          sortBy: JSON.stringify([["name", "ASC"]]),
        },
      ],
    ];
  }, [companyId]);

  const results = useQueries({
    queries: Resources.map(([key, execute, args]) => {
      return {
        queryKey: ShipmentResourceKeys.getResource(key, args),
        queryFn: async () => {
          const response = await execute(args);

          return response.data;
        },
        refetchOnWindowFocus: false,
        enabled: !!companyId,
        placeholderData: [],
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
      };
    }),
  });

  const [
    warehouseQuery,
    customerQuery,
    milestoneQuery,
    documentsQuery,
    portQuery,
    supplierQuery,
    officeQuery,
    notifyPartyQuery,
    countryQuery,
  ] = results;

  const loadingQueries = results.some((result) => result.isRefetching) || loadingShipmentNetwork;

  return (
    <ShipmentResourcesContext.Provider
      value={{
        resourceCompanyId: companyId,
        warehouseList: warehouseQuery.data,
        customerList: customerQuery.data,
        milestoneDates: milestoneQuery.data,
        companyDocuments: documentsQuery.data,
        supplierList: supplierQuery.data,
        portList: portQuery.data,
        officeList: officeQuery.data,
        notifyPartyList: notifyPartyQuery.data,
        countryList: countryQuery.data,
        loading: loadingQueries,
      }}
    >
      {children}
    </ShipmentResourcesContext.Provider>
  );
};

export { ShipmentResourcesContext, ShipmentResourcesProvider };

export const useShipmentResources = () => {
  const context = useContext(ShipmentResourcesContext);

  return context;
};
